import { Badge, Box, Button, Drawer, HStack, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import { Bot, Trash2, User, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { ChatMessage, PlaygroundAction } from '@/lib/api'
import { AiChatInput } from './AiChatInput'

interface AiChatDrawerProps {
  isOpen: boolean
  onClose: () => void
  messages: ChatMessage[]
  isLoading: boolean
  onSendMessage: (message: string, imageBase64?: string, imageMimeType?: string) => void
  onClearHistory: () => void
  lastActionSummary?: string | null
}

export function AiChatDrawer({
  isOpen,
  onClose,
  messages,
  isLoading,
  onSendMessage,
  onClearHistory,
  lastActionSummary,
}: AiChatDrawerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading])

  return (
    <Drawer.Root
      placement="end"
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
      modal={false}
      closeOnInteractOutside={false}
    >
      <Portal>
        <Drawer.Positioner pointerEvents="none">
          <Drawer.Content pointerEvents="auto" boxShadow="lg">
            {/* Header */}
            <Drawer.Header borderBottomWidth="1px" py={3}>
              <HStack justify="space-between" w="100%">
                <HStack gap={2}>
                  <Bot size={20} />
                  <Text fontWeight="bold" fontSize="lg">
                    AI Assistant
                  </Text>
                </HStack>
                <HStack gap={1}>
                  {messages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClearHistory}
                      title="Clear chat history"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    <X size={20} />
                  </Button>
                </HStack>
              </HStack>
            </Drawer.Header>

            {/* Messages Area */}
            <Drawer.Body p={0} display="flex" flexDirection="column">
              <Box flex={1} overflowY="auto" p={4} bg="bg.subtle">
                {messages.length === 0 ? (
                  <VStack
                    h="100%"
                    justify="center"
                    gap={4}
                    color="content.secondary"
                    textAlign="center"
                    px={6}
                  >
                    <Bot size={48} />
                    <VStack gap={2}>
                      <Text fontWeight="medium">AI Architecture Assistant</Text>
                      <Text fontSize="sm">
                        Ask me to help you build your architecture. I can add nodes, connect
                        components, or analyze architecture images.
                      </Text>
                      <VStack gap={1} mt={2} fontSize="xs" color="content.secondary">
                        <Text>Try:</Text>
                        <Text>"Add a HiveMQ broker"</Text>
                        <Text>"Connect it to a database"</Text>
                        <Text>"Add some PLCs on the left side"</Text>
                      </VStack>
                    </VStack>
                  </VStack>
                ) : (
                  <VStack gap={4} align="stretch">
                    {messages.map((msg, index) => (
                      <MessageBubble
                        key={index}
                        message={msg}
                        showActionSummary={
                          msg.role === 'assistant' &&
                          index === messages.length - 1 &&
                          !!lastActionSummary
                        }
                        actionSummary={lastActionSummary}
                      />
                    ))}
                    {isLoading && (
                      <HStack gap={3} p={3}>
                        <Box
                          w={8}
                          h={8}
                          borderRadius="full"
                          bg="yellow.100"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Bot size={16} />
                        </Box>
                        <Box bg="bg.panel" borderRadius="lg" px={4} py={3} boxShadow="sm">
                          <HStack gap={2}>
                            <Spinner size="sm" />
                            <Text fontSize="sm" color="content.secondary">
                              Thinking...
                            </Text>
                          </HStack>
                        </Box>
                      </HStack>
                    )}
                    <div ref={messagesEndRef} />
                  </VStack>
                )}
              </Box>

              {/* Input Area */}
              <AiChatInput onSend={onSendMessage} isLoading={isLoading} />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

interface MessageBubbleProps {
  message: ChatMessage
  showActionSummary?: boolean | null
  actionSummary?: string | null
}

function MessageBubble({ message, showActionSummary, actionSummary }: MessageBubbleProps) {
  const isUser = message.role === 'user'

  return (
    <HStack gap={3} align="flex-start" flexDirection={isUser ? 'row-reverse' : 'row'}>
      {/* Avatar */}
      <Box
        w={8}
        h={8}
        borderRadius="full"
        bg={isUser ? 'brand.subtle' : 'bg.subtle'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </Box>

      {/* Message Content */}
      <VStack align={isUser ? 'flex-end' : 'flex-start'} gap={1} maxW="80%">
        <Box
          bg={isUser ? 'brand.solid' : 'white'}
          color={isUser ? 'black' : 'inherit'}
          _dark={{
            bg: isUser ? 'brand.emphasized' : 'gray.700',
            color: isUser ? 'black' : 'white',
          }}
          borderRadius="lg"
          px={4}
          py={2}
          boxShadow="sm"
        >
          {isUser ? (
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {message.content}
            </Text>
          ) : (
            <Box
              fontSize="sm"
              className="markdown-content"
              css={{
                '& p': { margin: 0 },
                '& p + p': { marginTop: '0.5em' },
                '& code': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  padding: '0.1em 0.3em',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                },
                '& pre': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  padding: '0.5em',
                  borderRadius: '4px',
                  overflow: 'auto',
                },
                '& ul, & ol': {
                  marginLeft: '1.5em',
                  marginTop: '0.5em',
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            </Box>
          )}
        </Box>

        {/* Action Summary Badge */}
        {showActionSummary && actionSummary && (
          <Badge colorPalette="green" variant="subtle" fontSize="xs" px={2} py={1}>
            {actionSummary}
          </Badge>
        )}
      </VStack>
    </HStack>
  )
}

export default AiChatDrawer
